import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import { ShellMessage, Row } from './shellHelpers';

/**
 * Printable order receipt.
 *
 * The print stylesheet hides the page chrome so a customer (or a brand
 * owner exporting receipts for accounting) can hit Cmd/Ctrl+P and get
 * a clean A4 page. Intentionally does NOT render `StorefrontNav` so
 * print output starts with the brand header.
 */
export default function StorefrontOrderReceipt() {
  const { orderId } = useParams();
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const accent = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand?._id || !orderId) return;
    setLoading(true);
    StorefrontAPI.getMyOrder(brand._id, orderId)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [brand?._id, orderId]);

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{`
        @media print {
          .receipt-actions { display: none !important; }
          body { background: #fff !important; }
        }
        @page { margin: 16mm; }
      `}</style>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 20px' }}>
        <div className='receipt-actions' style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(brandPath(brand, `/order/${orderId}`))}
            style={{
              background: 'transparent', border: '1px solid #e5e7eb',
              padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
              color: '#475569', fontSize: 13, fontWeight: 600,
            }}
          >
            ← Back to order
          </button>
          <button
            onClick={() => window.print()}
            style={{
              background: accent, color: 'white', border: 'none',
              padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Print
          </button>
        </div>

        {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
        {!loading && !order && <div style={{ color: '#64748b' }}>Order not found.</div>}
        {!loading && order && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: 14, marginBottom: 20 }}>
              <div>
                {brand.logo_upload_path && (
                  <img src={brand.logo_upload_path} alt='' style={{ height: 40, marginBottom: 6 }} />
                )}
                <div style={{ fontSize: 20, fontWeight: 700 }}>{brand.name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Receipt</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{order.number || ''}</div>
                <div style={{ fontSize: 12, color: '#475569' }}>
                  {order.placed_at ? new Date(order.placed_at).toLocaleString() : ''}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                  Billed to
                </div>
                <div style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.5 }}>
                  <div>{order.shipping_address?.name || ''}</div>
                  <div>{order.shipping_address?.line1 || ''}</div>
                  {order.shipping_address?.line2 && <div>{order.shipping_address.line2}</div>}
                  <div>
                    {[order.shipping_address?.city, order.shipping_address?.region, order.shipping_address?.postal_code]
                      .filter(Boolean).join(', ')}
                  </div>
                  <div>{order.shipping_address?.country || ''}</div>
                  {order.shipping_address?.email && (
                    <div style={{ marginTop: 4, color: '#475569' }}>{order.shipping_address.email}</div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                  Status
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{order.status}</div>
                {order.paid_at && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                    Paid {new Date(order.paid_at).toLocaleString()}
                  </div>
                )}
                {(order.tracking_carrier || order.tracking_number) && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                    {order.tracking_carrier} · {order.tracking_number}
                  </div>
                )}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px', fontSize: 11, color: '#475569', textTransform: 'uppercase' }}>Item</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, color: '#475569', textTransform: 'uppercase', textAlign: 'right' }}>Qty</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, color: '#475569', textTransform: 'uppercase', textAlign: 'right' }}>Price</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, color: '#475569', textTransform: 'uppercase', textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(order.line_items || []).map((li, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 600 }}>{li.product_name}</div>
                      {li.variant_name && <div style={{ fontSize: 12, color: '#64748b' }}>{li.variant_name}</div>}
                      {li.sku && <div style={{ fontSize: 11, color: '#94a3b8' }}>SKU {li.sku}</div>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{li.quantity}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatMoney(li.unit_price, order.currency)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{formatMoney(li.line_total, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: 280, fontSize: 13 }}>
                <Row label='Subtotal'>{formatMoney(order.subtotal, order.currency)}</Row>
                {Number(order.discount_total || 0) > 0 && (
                  <Row label='Discount'>-{formatMoney(order.discount_total, order.currency)}</Row>
                )}
                {Number(order.shipping_total || 0) > 0 && (
                  <Row label='Shipping'>{formatMoney(order.shipping_total, order.currency)}</Row>
                )}
                {Number(order.tax_total || 0) > 0 && (
                  <Row label='Tax'>{formatMoney(order.tax_total, order.currency)}</Row>
                )}
                <div style={{ borderTop: '2px solid #0f172a', paddingTop: 6, marginTop: 6 }}>
                  <Row label='Total'><strong style={{ fontSize: 15 }}>{formatMoney(order.grand_total, order.currency)}</strong></Row>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 28, paddingTop: 14, borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              Thank you for shopping with {brand.name}.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
